import React, {useState, useEffect} from 'react'
import {useRouter} from 'next/router'
import {BackIcon, Button} from '@ambler/andive-next'
import {useProduct} from './product'

export const CanGoBackContext = React.createContext(null)

/*
 * This provider computes a rough history in order to handle back button logic
 * It uses its own history as landing on a page triggers an
 * routeChangeComplete event making the variable canGoBack always true
 * The provider being placed on _app, its state may not preserved on Fast Refresh
 * so it should be used carefully with a fallback option for development NODE_ENV
 * We also use history as a supplementary condition, as it can only increase
 */
export const CanGoBackProvider: React.FC = ({children}) => {
  const router = useRouter()
  const [routerHistory, setRouterHistory] = useState<string[]>([router.asPath])
  const [canGoBack, setCanGoBack] = useState<boolean>(false)

  const onRouteChangeComplete = React.useCallback(
    (url: string) => {
      let nextHistory = [...routerHistory]
      if (routerHistory.length > 1 && routerHistory[routerHistory.length - 2] === url) {
        nextHistory = routerHistory.slice(0, routerHistory.length - 1)
      } else if (routerHistory[routerHistory.length - 1] !== url) {
        nextHistory = [...nextHistory, url]
      }
      setRouterHistory(nextHistory)
      setCanGoBack(nextHistory.length > 1 && history.length > 1)
    },
    [routerHistory],
  )

  useEffect(() => {
    if (!router) {
      return undefined
    }
    router.events.on('routeChangeComplete', onRouteChangeComplete)
    return () => {
      router.events.off('routeChangeComplete', onRouteChangeComplete)
    }
  }, [onRouteChangeComplete, router])

  return (
    <CanGoBackContext.Provider
      value={{
        canGoBack,
      }}
    >
      {children}
    </CanGoBackContext.Provider>
  )
}

export const BackButton = ({onClick, fallbackTo}: {onClick?: () => void; fallbackTo?: string}) => {
  const router = useRouter()
  const product = useProduct()

  const {canGoBack: hasHistory} = React.useContext(CanGoBackContext)

  const defaultFallBack = product === 'BO' ? '/' : '/a4d/account'

  const onClick_ = () => {
    if (onClick) {
      return onClick()
    }

    if (hasHistory) {
      return router.back()
    }

    return router.replace(fallbackTo ?? defaultFallBack)
  }

  return (
    <Button variant="third" type="button" onClick={onClick_}>
      <Button.Icon icon={<BackIcon />} />
    </Button>
  )
}
